/**
 * Consult Door B — Firebase App Check bootstrap (flip-on-ready).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY
 * ─────────────────────────────────────────────────────────────────────────
 *  The Door B page (`/book/...`) is the FIRST portal surface anyone on the
 *  public internet can hit with no login. Its two public Cloud Functions
 *  (`getPublicAvailability`, `createPublicBooking`) are App-Check-gated
 *  (spec R5 · datamap §3, Vex hard-gate S-1). App Check attaches a bot-shield
 *  token to every callable request once initialised — the Functions SDK wires
 *  it automatically, so all this module has to do is turn it on.
 *
 *  OWNER-PROVISIONED LATER: the reCAPTCHA v3 site key is created in the
 *  Firebase console by Rohit (~15 min) — it is the one switch that cannot be
 *  flipped from code. So we NEVER hardcode it: we read it from Vite env
 *  (`VITE_APPCHECK_SITE_KEY`). Until it is set, `ensureAppCheck()` is a no-op
 *  and the page still renders/degrades honestly (the live callables simply
 *  aren't reachable yet). The day the key lands in `.env`, App Check enforces
 *  with zero code change — "flip-on-ready".
 *
 *  TEST / SSR SAFE: guards `import.meta` (undefined under the Node runner) and
 *  `window`, and lazy-imports the App Check SDK so the pure consulting layer
 *  never drags it into tests.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Read the owner-provisioned reCAPTCHA v3 site key from Vite env. Empty when
 *  not yet configured (App Check stays off — flip-on-ready). */
export function appCheckSiteKey(): string {
  if (typeof import.meta === 'undefined') return '';
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const key = env?.VITE_APPCHECK_SITE_KEY;
  return typeof key === 'string' ? key.trim() : '';
}

/** True once App Check has been initialised in this tab. */
let initialised = false;
let inflight: Promise<boolean> | null = null;
/** The live App Check handle from `initializeAppCheck` — kept so we can fetch a
 *  token with our own timeout (see `getAppCheckTokenBounded`). Null until init. */
let appCheckInstance: import('firebase/app-check').AppCheck | null = null;

/**
 * Bounded App-Check-token result. `unconfigured` means App Check is off (no site
 * key / `ensureAppCheck` no-opped) — the caller should proceed WITHOUT a token
 * (flip-on-ready). `timeout`/`error` mean the reCAPTCHA token could not be
 * obtained (the classic in-app-WebView hang) — the caller must NOT invoke the
 * callable, or the Functions SDK will fetch the token itself and hang forever.
 */
export type AppCheckTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: 'timeout' | 'error' | 'unconfigured' };

/**
 * Initialise Firebase App Check with the reCAPTCHA v3 provider IF the site key
 * is configured. Idempotent (safe to call before every public callable). Returns
 * whether App Check is active. A missing key, an SDK-load failure or any init
 * error resolves to `false` WITHOUT throwing — the page must keep working and
 * surface the honest backend state, never crash on a bot-shield that isn't wired
 * yet.
 */
export async function ensureAppCheck(): Promise<boolean> {
  if (initialised) return true;
  if (typeof window === 'undefined') return false;
  const siteKey = appCheckSiteKey();
  if (!siteKey) return false; // not provisioned yet → App Check off (flip-on-ready)
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const [{ app }, { initializeAppCheck, ReCaptchaV3Provider }] =
        await Promise.all([
          import('../../../lib/firebase'),
          import('firebase/app-check'),
        ]);
      appCheckInstance = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      initialised = true;
      return true;
    } catch {
      // SDK unavailable / already initialised elsewhere / bad key → stay off.
      return false;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Fetch an App Check token with a HARD upper bound (default 5s).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  Inside some in-app browsers (notably WhatsApp's iOS WKWebView) the
 *  reCAPTCHA v3 token promise can NEVER settle. The Functions SDK fetches that
 *  token internally before it sends a callable, with no timeout — so an
 *  unsettled token turns a booking call into a permanent hang (infinite
 *  spinner, no error, no retry). We can't shorten the SDK's own internal wait,
 *  so instead we PRE-FETCH the token ourselves with `Promise.race` against a
 *  timeout. If we get a token, the SDK reuses it from its cache (no second
 *  wait); if we time out, the caller skips the callable entirely and shows a
 *  friendly "open in your browser" fallback instead of hanging.
 *
 *  App Check enforcement is UNCHANGED — this is purely client-side resilience.
 *  `unconfigured` (no site key) is NOT a failure: the caller proceeds without a
 *  token exactly as before (flip-on-ready), and the honest backend state shows.
 */
export async function getAppCheckTokenBounded(ms = 5000): Promise<AppCheckTokenResult> {
  const active = await ensureAppCheck();
  if (!active || !appCheckInstance) return { ok: false, reason: 'unconfigured' };

  try {
    const { getToken } = await import('firebase/app-check');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeoutP = new Promise<'__timeout'>((resolve) => {
      timer = setTimeout(() => resolve('__timeout'), ms);
    });
    const race = await Promise.race([getToken(appCheckInstance, false), timeoutP]);
    if (timer) clearTimeout(timer);
    if (race === '__timeout') return { ok: false, reason: 'timeout' };
    const token = (race as { token?: unknown }).token;
    if (typeof token === 'string' && token) return { ok: true, token };
    return { ok: false, reason: 'error' };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** For diagnostics / the honest "verifying your browser" copy: is the shield
 *  configured at all yet? (Distinct from "initialised".) */
export function appCheckConfigured(): boolean {
  return appCheckSiteKey().length > 0;
}
